'use client';

import { useContext, createContext, FC, type ReactNode, useState } from 'react';
import type { AdminAIProviderProps } from './AdminAIProvider.types';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { useDownloadNotification } from '@/_utils/notifications';
import download from 'downloadjs';
import { utils, write } from 'xlsx';
import { useRouter } from 'next/navigation';

const AdminAIContext = createContext<AdminAIProviderProps>({
  downloadNotificationAttachment: () => Promise.resolve(false),
  downloadContentAs: (_content: string, _name: string, _type: 'xls') =>
    Promise.resolve(false),
  loadingQuestionnaireId: undefined,
  openPreviewQuestionnaire: () => {},
});

const AdminAIProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [loadingQuestionnaireId, setLoadingQuestionnaireId] = useState<
    number | undefined
  >(undefined);

  const { push } = useRouter();
  const downloadNotification = useDownloadNotification();

  const { setHighlightedQuestionIds } = useContext(QuestionnaireContext);
  const { token } = useContext(UserContext);

  const downloadContentAs = (
    content: string,
    name: string = 'file',
    type: 'xls',
  ): void => {
    try {
      switch (type) {
        case 'xls': {
          const csvRows: Array<Array<string>> = content
            .split('\n')
            .map((row) => row.split(','));
          const workbook = utils.book_new();
          const worksheet = utils.aoa_to_sheet(csvRows);
          utils.book_append_sheet(workbook, worksheet, 'Sheet1');

          const excelBuffer = write(workbook, {
            bookType: 'xls',
            type: 'array',
          });

          download(
            new Blob([excelBuffer], {
              type: 'application/vnd.ms-excel',
            }),
            `${name}.${type}`,
            'application/vnd.ms-excel',
          );
          break;
        }
        default:
          console.error(`Unsupported file type: ${type}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const openPreviewQuestionnaire = async (
    questionnaireId: number,
    questionId:
      | number
      | string
      | Array<number | string>
      | undefined = undefined,
  ) => {
    if (!token) return;

    try {
      setLoadingQuestionnaireId(questionnaireId);
      push(`/questionnaires/${questionnaireId}`);

      const parseId = (id: number | string): number => {
        const parsed = typeof id === 'string' ? parseInt(id, 10) : id;

        return isNaN(parsed) ? 0 : parsed;
      };

      const newHighlightedQuestionIds: Array<number> = Array.isArray(questionId)
        ? questionId.map(parseId).filter((id) => id > 0)
        : questionId !== undefined
          ? [parseId(questionId)].filter((id) => id > 0)
          : [];
      setHighlightedQuestionIds(newHighlightedQuestionIds);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingQuestionnaireId(undefined);
    }
  };

  return (
    <AdminAIContext
      value={{
        downloadContentAs,
        downloadNotificationAttachment: downloadNotification,
        loadingQuestionnaireId,
        openPreviewQuestionnaire,
      }}
    >
      {children}
    </AdminAIContext>
  );
};

export { AdminAIContext, AdminAIProvider };
